import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth, SKIP_AUTH_HEADER } from './baseQuery';
import type {
    LoginRequest,
    LoginResponse,
    LoginVerifyRequest,
    LoginVerifyResponse,
    RegisterRequest,
    RegisterResponse,
    User,
    SendOtpRequest,
    SendOtpResponse,
    VerifyOtpRequest,
    VerifyOtpResponse,
    CreateInvestigationRequest,
    CreateInvestigationResponse,
    CreateInvestigationBatchRequest,
    CreateInvestigationBatchResponse,
    ValidateInvestigationResponse,
    PaymentExemptionRequest,
    ConsentDraft,
    SaveConsentDraftRequest,
    RetriggerConsentResponse,
    NotaryInvestigationListItem,
    NotaryInvestigationDetail,
    PoliceInvestigationListItem,
    PoliceInvestigationDetail,
    RequestableField,
    InformationRequest,
    CreateInformationRequest,
    SubmitInformationRequest,
    NudgeRequest,
    NudgeResponse,
    InvestigationGuarantor,
    SubmitGuarantorsRequest,
    SubmitGuarantorsResponse,
    GuarantorRecommendationRequest,
    GuarantorIndemnityForm,
    SubmitGuarantorIndemnityRequest,
    SubmitGuarantorIndemnityResponse,
    SubmitGuarantorRecommendationRequest,
    SubmitGuarantorRecommendationResponse,
    AvailableAgent,
    UpdateGuarantorVerificationRequest,
    Investigation,
    InvestigationStats,
    SubmitReportRequest,
    InvestigationDocument,
    DocumentValidationResponse,
    InitiatePaymentRequest,
    InitiatePaymentResponse,
    VerifyPaymentRequest,
    VerifyPaymentResponse,
    Transaction,
    ReusablePayment,
    Earning,
    EarningsSummary,
    EarningsBreakdown,
    PlatformEarningsStats,
    MarkEarningPaidResponse,
    BulkMarkPaidRequest,
    BulkMarkPaidResponse,
    DashboardStats,
    InvestigationTrend,
    RevenueTrend,
    TopAgent,
    StatusDistribution,
    FinancialStats,
    InviteAgentRequest,
    CreateStaffRequest,
    AdminInvestigationFilters,
    PricingConfig,
    SelectionQuoteRequest,
    SelectionQuoteResponse,
    AdminPlanFeature,
    CreatePlanFeatureRequest,
    UpdatePlanFeatureRequest,
    AdminCheckAddon,
    CreateCheckAddonRequest,
    UpdateCheckAddonRequest,
    UpdatePricingRequest,
    AdminPlan,
    UpdatePlanRequest,
    PromoCode,
    CreatePromoCodeRequest,
    UpdatePromoCodeRequest,
    PromoRedemption,
    PromoQuoteRequest,
    PromoQuoteResponse,
    SendSmsRequest,
    SendSmsResponse,
    AdminTransactionFilters,
    AdminTransactionsResponse,
    UserWithStats,
    UpdateUserStatusRequest,
    ReassignInvestigationRequest,
    PlatformStatistics,
    CallbackRequestPayload,
    CallbackRequestResponse,
    Callback,
    CallbackListParams,
    CallbackStatus,
    Paginated,
    WaitlistEntry,
    WaitlistResponse,
    WaitlistStats,
    CheckEmailResponse,
    UpdateWaitlistRequest,
    BulkUpdateWaitlistRequest,
    DocumentType,
    EarningStatus,
    CheckOption,
    EmployerReportPayload,
    InvestigationAddressVisit,
    CreateAddressVisitRequest,
    ChecklistCatalogResponse,
    InvestigationChecklist,
    SaveChecklistRequest,
    InvestigationMilestonesResponse,
    SaveMilestoneDraftRequest,
    SubmitMilestoneRequest,
    CheckType,
    CheckTypeBriefsResponse,
    InvestigationAgentBrief,
    ScoringCriteriaResponse,
    RiskAssessmentSubmission,
    RiskAssessmentResponse,
    DocumentApprovalRequest,
    SupportTicket,
    SubmitFormResponse,
    EmployeeOpenTicketRequest,
    EmployeeReplyRequest,
    AdminReplyRequest,
    OpenEmployerTicketRequest,
    EmployerReplyRequest,
    CreateTicketRequest,
    UpdateTicketStatusRequest,
    UpdateTicketPriorityRequest,
    AssignTicketRequest,
    TicketStatistics,
    TicketStatus,
    TicketPriority,
    TicketCategory,
    Blog,
    BlogListItem,
    PaginatedBlogs,
    BlogListParams,
    CreateBlogRequest,
    UpdateBlogRequest,
    VerificationResult,
    EmployerReferencePublic,
    SubmitEmployerReferenceRequest,
    EmployerReference,
    BankAccount,
    AddBankAccountRequest,
    UpdateBankAccountRequest,
    Withdrawal,
    CreateWithdrawalRequest,
    ReviewWithdrawalRequest,
    ProcessWithdrawalRequest,
    WithdrawalStatistics,
    WithdrawalStatus,
    InvestigationTimer,
    SlaSummary,
    HrMeResponse,
    HrReferral,
    HrEarning,
    HrEarningsSummary,
    HrEarningStatus,
    HrLeaderboardEntry,
    TrainingCourseSummary,
    TrainingCourseDetail,
    EnrollCourseResponse,
    MarkWatchedResponse,
    VideoQuiz,
    QuizAnswer,
    SubmitQuizResponse,
    TrainingCertificate,
    AdminTrainingCourse,
    AdminTrainingVideo,
    AdminTrainingQuestion,
    CreateCourseRequest,
    UpdateCourseRequest,
    AddVideoLinkRequest,
    UpdateVideoRequest,
    AddQuestionRequest,
    UpdateQuestionRequest,
    DeletedResponse,
    ContactMessage,
    SubmitContactMessageRequest,
    UpdateContactMessageStatusRequest,
    AssignMedicalResponse,
    CsChecklist,
    UpdateCsChecklistRequest,
    MessageSentResponse,
    NotifyMessageRequest,
    MedicalTestPanel,
    InvestigationMedicalReport,
    SubmitMedicalReportRequest,
    CsMetrics,
    MedicalMetrics,
    MedicalLab,
    MedicalLabFilters,
    AssignLabResponse,
    CreateMedicalLabRequest,
    UpdateMedicalLabRequest,
    EmployeeReport,
    EmployeeReportScanMatch,
    EmployeeReportsResponse,
    EmployeeReportListParams,
    EmployeeReportScanParams,
    FileEmployeeReportRequest,
    UpdateEmployeeReportStatusRequest,
} from '../../types';

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Auth',
        'User',
        'Investigation',
        'Transaction',
        'Earning',
        'Analytics',
        'Admin',
        'SupportTicket',
        'BankAccount',
        'Withdrawal',
        'Waitlist',
        'RiskAssessment',
        'Guarantor',
        'CheckOptions',
        'HrReferral',
        'HrEarning',
        'HrLeaderboard',
        'HrMe',
        'Training',
        'Certificate',
        'ContactMessage',
        'CustomerSuccess',
        'Medical',
        'Pricing',
        'MedicalLab',
        'Plan',
        'PromoCode',
        'AdminTransaction',
        'InformationRequest',
        'Notary',
        'CsChecklist',
        'Blog',
        'EmployerReference',
        'Police',
        'EmployeeReport',
        'Callback',
        'CheckAddon',
        'PlanFeature',
    ],
    endpoints: (builder) => ({
        // ==================== AUTHENTICATION ====================
        login: builder.mutation<LoginResponse, LoginRequest>({
            query: (credentials) => ({
                url: '/auth/login',
                method: 'POST',
                body: credentials,
            }),
            invalidatesTags: ['Auth'],
        }),

        loginVerify: builder.mutation<LoginVerifyResponse, LoginVerifyRequest>({
            query: (data) => ({
                url: '/auth/login/verify',
                method: 'POST',
                body: data,
            }),
            // Bust CheckOptions too — the response is referral-aware and we don't
            // want stale anonymous prices after the user authenticates.
            invalidatesTags: ['Auth', 'CheckOptions'],
        }),

        register: builder.mutation<RegisterResponse, RegisterRequest>({
            query: (userData) => ({
                url: '/auth/register',
                method: 'POST',
                body: userData,
            }),
        }),

        getProfile: builder.query<User, void>({
            query: () => '/auth/profile',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['Auth'],
        }),

        sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
            query: (data) => ({
                url: '/auth/send-otp',
                method: 'POST',
                body: data,
            }),
        }),

        verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
            query: (data) => ({
                url: '/auth/verify-otp',
                method: 'POST',
                body: data,
            }),
            // Signup → OTP verify is the moment the user becomes authenticated, so
            // bust CheckOptions so any cached anonymous prices are refetched.
            invalidatesTags: ['Auth', 'CheckOptions'],
        }),

        sendEmailVerification: builder.mutation<{ success: boolean; message: string }, void>({
            query: () => ({
                url: '/auth/send-email-verification',
                method: 'POST',
            }),
        }),

        verifyEmail: builder.mutation<{ success: boolean; message: string }, { code: string }>({
            query: (data) => ({
                url: '/auth/verify-email',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Auth', 'CheckOptions'],
        }),

        // ==================== INVESTIGATIONS ====================
        validateInvestigation: builder.mutation<ValidateInvestigationResponse, CreateInvestigationRequest>({
            query: (data) => ({
                url: '/investigations/validate',
                method: 'POST',
                body: data,
            }),
        }),

        // Buy checks on several people at once: one plan, one payment. Documents
        // are not sent here — they are uploaded per person afterwards, exactly as
        // in the single flow.
        createInvestigationBatch: builder.mutation<CreateInvestigationBatchResponse, CreateInvestigationBatchRequest>({
            query: (data) => ({
                url: '/investigations/batch',
                method: 'POST',
                body: data,
            }),
            // A complimentary batch is settled server-side, so the lists change
            // immediately. A payable one redirects to the gateway.
            invalidatesTags: (result) => (result?.isPaid ? ['Investigation', 'Transaction', 'Analytics'] : []),
        }),

        createInvestigation: builder.mutation<
            CreateInvestigationResponse,
            { data: CreateInvestigationRequest; documents?: File[]; documentTypes?: string[] }
        >({
            query: ({ data, documents, documentTypes }) => {
                // If documents are provided, use FormData
                if (documents && documents.length > 0) {
                    const formData = new FormData();

                    // Append all investigation data fields
                    formData.append('checkType', data.checkType);
                    formData.append('employeeFirstName', data.employeeFirstName);
                    if (data.employeeMiddleName) formData.append('employeeMiddleName', data.employeeMiddleName);
                    formData.append('employeeLastName', data.employeeLastName);
                    formData.append('employeeEmail', data.employeeEmail);
                    formData.append('employeePhone', data.employeePhone);
                    if (data.employeeGender) formData.append('employeeGender', data.employeeGender);
                    if (data.transactionId) formData.append('transactionId', data.transactionId);
                    if (data.investigationCategory)
                        formData.append('investigationCategory', data.investigationCategory);
                    if (data.medicalAnalysisIncluded) formData.append('medicalAnalysisIncluded', 'true');

                    // Append documents
                    documents.forEach((file) => {
                        formData.append('documents', file);
                    });

                    // Append document types if provided
                    if (documentTypes) {
                        formData.append('documentTypes', documentTypes.join(','));
                    }

                    return {
                        url: '/investigations',
                        method: 'POST',
                        body: formData,
                    };
                }

                // Otherwise use JSON
                return {
                    url: '/investigations',
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: ['Investigation', 'Analytics'],
        }),

        getMyInvestigations: builder.query<Investigation[], { isPaid?: boolean } | void>({
            query: (args) => {
                const params: Record<string, string> = {};
                // Backend only accepts the literal strings "true"/"false". Skip the param
                // entirely when undefined so we get the full list.
                if (args && typeof args.isPaid === 'boolean') {
                    params.isPaid = args.isPaid ? 'true' : 'false';
                }
                return {
                    url: '/investigations/my-investigations',
                    params,
                };
            },
            // Always resolve to an array — some roles/responses return an empty or
            // non-array body, and consumers call .filter/.map on the result.
            transformResponse: (response: any) => {
                if (Array.isArray(response)) return response;
                if (Array.isArray(response?.data)) return response.data;
                return [];
            },
            providesTags: ['Investigation'],
        }),

        getInvestigationById: builder.query<Investigation, string>({
            query: (id) => `/investigations/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        getEmployerReport: builder.query<EmployerReportPayload, string>({
            query: (id) => `/investigations/${id}/employer-report`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        // ==================== ADDRESS VISITS ====================

        getAddressVisits: builder.query<InvestigationAddressVisit[], string>({
            query: (id) => `/investigations/${id}/address-visits`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        createAddressVisit: builder.mutation<
            InvestigationAddressVisit,
            { id: string; data: CreateAddressVisitRequest }
        >({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/address-visits`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        deleteAddressVisit: builder.mutation<
            { success: boolean } | InvestigationAddressVisit,
            { id: string; visitId: string }
        >({
            query: ({ id, visitId }) => ({
                url: `/investigations/${id}/address-visits/${visitId}/delete`,
                method: 'PATCH',
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // ==================== INVESTIGATOR CHECKLIST ====================

        // Pass a checkType to scope the catalog to a single tier; omit to get
        // the full unfiltered catalog (admin overviews, marketing pages).
        getChecklistCatalog: builder.query<ChecklistCatalogResponse, { checkType?: CheckType } | void>({
            query: (args) => {
                const url = '/investigations/checklist-catalog';
                if (args && args.checkType) {
                    return { url, params: { checkType: args.checkType } };
                }
                return { url };
            },
        }),

        // Marketing / pricing-card source of truth for all check tiers.
        getCheckTypeBriefs: builder.query<CheckTypeBriefsResponse, void>({
            query: () => '/investigations/check-types',
        }),

        // Per-investigation scope of work — used on the agent landing card.
        getInvestigationAgentBrief: builder.query<InvestigationAgentBrief, string>({
            query: (id) => `/investigations/${id}/agent-brief`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        getInvestigationChecklist: builder.query<InvestigationChecklist, string>({
            query: (id) => `/investigations/${id}/checklist`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        saveInvestigationChecklist: builder.mutation<
            InvestigationChecklist,
            { id: string; data: SaveChecklistRequest }
        >({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/checklist`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // ==================== MILESTONES ====================
        // Role-aware: agents get full content (summary/details/items); employers
        // and admins get progress-only milestones (the server strips fields).
        getInvestigationMilestones: builder.query<InvestigationMilestonesResponse, string>({
            query: (id) => `/investigations/${id}/milestones`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        // Agent-only. Saves a draft of summary/details/answers for one section.
        // Any subset of fields is allowed; an empty body just touches updatedAt.
        saveMilestoneDraft: builder.mutation<
            InvestigationMilestonesResponse,
            { id: string; sectionId: string; data: SaveMilestoneDraftRequest }
        >({
            query: ({ id, sectionId, data }) => ({
                url: `/investigations/${id}/milestones/${sectionId}/draft`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // Agent-only. Locks the milestone after server-side gates pass (summary +
        // details non-empty, every section item answered).
        submitMilestone: builder.mutation<
            InvestigationMilestonesResponse,
            { id: string; sectionId: string; data: SubmitMilestoneRequest }
        >({
            query: ({ id, sectionId, data }) => ({
                url: `/investigations/${id}/milestones/${sectionId}/submit`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // Super-admin only. Removes a milestone section from this investigation
        // (drops it from checklist/milestones/report/progress and deletes its
        // stored work). Also invalidates the checklist so its gate updates.
        removeMilestoneSection: builder.mutation<InvestigationMilestonesResponse, { id: string; sectionId: string }>({
            query: ({ id, sectionId }) => ({
                url: `/investigations/${id}/milestones/${sectionId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Investigation'],
        }),

        // Super-admin only. Adds a milestone section: restores a removed default
        // section or opts in a catalog section outside the check type's scope.
        addMilestoneSection: builder.mutation<InvestigationMilestonesResponse, { id: string; sectionId: string }>({
            query: ({ id, sectionId }) => ({
                url: `/investigations/${id}/milestones/${sectionId}`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Investigation'],
        }),

        getInvestigationByLink: builder.query<Investigation, string>({
            query: (linkToken) => `/investigations/by-link/${linkToken}`,
        }),

        // ==================== CONSENT DRAFT (save & return) ====================
        // Public (link-token) draft of the employee questionnaire so they can
        // resume later on any device. `data` is null when no draft exists yet.
        getConsentDraft: builder.query<ConsentDraft | null, string>({
            query: (linkToken) => `/investigations/by-link/${linkToken}/draft`,
            transformResponse: (response: any) => {
                if (response?.data !== undefined && response?.success !== undefined) return response.data ?? null;
                return response ?? null;
            },
        }),

        saveConsentDraft: builder.mutation<ConsentDraft, SaveConsentDraftRequest>({
            query: ({ linkToken, formData, currentStep }) => ({
                url: `/investigations/by-link/${linkToken}/draft`,
                method: 'PATCH',
                body: { formData, currentStep },
            }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        submitEmployeeForm: builder.mutation<SubmitFormResponse, { linkToken: string; data: any }>({
            query: ({ linkToken, data }) => ({
                url: `/investigations/submit-form/${linkToken}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Investigation'],
        }),

        // Super Admin only — permanently deletes an investigation and all owned data.
        // Irreversible; linked transactions/tickets/earnings are detached, not deleted.
        deleteInvestigation: builder.mutation<{ id: string; deleted: boolean }, string>({
            query: (id) => ({
                url: `/investigations/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Investigation', 'Analytics'],
        }),

        // Super Admin only — rotates the consent link (old token dies), resets consent
        // state, and re-sends the invitation. Only valid for not-yet-submitted forms.
        retriggerConsentForm: builder.mutation<RetriggerConsentResponse, string>({
            query: (id) => ({
                url: `/investigations/${id}/retrigger-consent`,
                method: 'POST',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Investigation'],
        }),

        // ==================== INFORMATION REQUESTS (missing info) ====================
        // Admin/CS: catalog of requestable fields (grouped by `section` on the client).
        getRequestableFields: builder.query<RequestableField[], void>({
            query: () => '/investigations/requestable-fields',
            transformResponse: (response: any) => (Array.isArray(response) ? response : (response?.data ?? [])),
        }),

        // Admin/CS: history of information requests for one investigation (newest first).
        getInformationRequests: builder.query<InformationRequest[], string>({
            query: (id) => `/investigations/${id}/information-requests`,
            transformResponse: (response: any) => (Array.isArray(response) ? response : (response?.data ?? [])),
            providesTags: (_result, _err, id) => [{ type: 'InformationRequest', id }],
        }),

        // Admin/CS: create a request (emails the employee a focused form link).
        createInformationRequest: builder.mutation<InformationRequest, CreateInformationRequest>({
            query: ({ id, fields, note }) => ({
                url: `/investigations/${id}/information-requests`,
                method: 'POST',
                body: { fields, ...(note ? { note } : {}) },
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _err, { id }) => [{ type: 'InformationRequest', id }],
        }),

        // Public (token from email link) — load the focused form. 404 on bad token.
        getInformationRequestByToken: builder.query<InformationRequest, string>({
            query: (token) => `/investigations/information-request/${token}`,
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // Public — submit the values. Only send keys that were requested.
        // When document files are present, send multipart/form-data (files keyed by
        // field key + a JSON `values` field); otherwise a plain JSON body.
        submitInformationRequest: builder.mutation<InformationRequest, SubmitInformationRequest>({
            query: ({ token, values, documents }) => {
                const url = `/investigations/information-request/${token}`;
                const docEntries = Object.entries(documents ?? {}).filter(([, file]) => file instanceof File);
                if (docEntries.length === 0) {
                    return { url, method: 'PATCH', body: { values } };
                }
                const fd = new FormData();
                fd.append('values', JSON.stringify(values));
                for (const [key, file] of docEntries) fd.append(key, file);
                return { url, method: 'PATCH', body: fd };
            },
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // Admin/CS: nudge employee/employer/both by email.
        nudgeInvestigation: builder.mutation<NudgeResponse, NudgeRequest>({
            query: ({ id, target, message }) => ({
                url: `/investigations/${id}/nudge`,
                method: 'POST',
                body: { target, ...(message ? { message } : {}) },
            }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== GUARANTORS ====================
        // Employee submits guarantors (public, same linkToken as the consent flow).
        // Count must equal the plan requirement (Basic = 1, Standard/Pro = 2) or the
        // backend returns 400.
        submitGuarantors: builder.mutation<SubmitGuarantorsResponse, SubmitGuarantorsRequest>({
            query: ({ linkToken, guarantors }) => ({
                url: `/investigations/submit-guarantors/${linkToken}`,
                method: 'POST',
                body: { guarantors },
            }),
            invalidatesTags: ['Investigation', 'Guarantor'],
        }),

        // ===== GUARANTOR RECOMMENDATION (public — token from the guarantor's email/SMS) =====
        // The path /guarantor-recommendation/:token is baked into outgoing emails/SMS.
        getGuarantorRecommendation: builder.query<GuarantorRecommendationRequest, string>({
            query: (token) => `/investigations/guarantor-recommendation/${token}`,
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // One submission per link — a second POST returns 400 (terminal, not retryable).
        submitGuarantorRecommendation: builder.mutation<
            SubmitGuarantorRecommendationResponse,
            SubmitGuarantorRecommendationRequest
        >({
            query: ({ token, relationshipConfirmed, recommendation }) => ({
                url: `/investigations/guarantor-recommendation/${token}`,
                method: 'POST',
                // ONLY these two keys — the API runs forbidNonWhitelisted.
                body: { relationshipConfirmed, recommendation },
            }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ===== GUARANTOR INDEMNITY FORM (public — same token as the link above) =====
        // Served at the route already baked into outgoing email/SMS, so every link
        // sitting in an inbox keeps working and no backend redeploy is needed.
        getGuarantorIndemnityForm: builder.query<GuarantorIndemnityForm, string>({
            // No Authorization header: the token in the path IS the credential.
            query: (token) => ({
                url: `/investigations/guarantor-indemnity/${token}`,
                headers: { [SKIP_AUTH_HEADER]: '1' },
            }),
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Guarantor'],
        }),

        submitGuarantorIndemnity: builder.mutation<
            SubmitGuarantorIndemnityResponse,
            { token: string; data: SubmitGuarantorIndemnityRequest }
        >({
            query: ({ token, data }) => ({
                url: `/investigations/guarantor-indemnity/${token}`,
                method: 'POST',
                headers: { [SKIP_AUTH_HEADER]: '1' },
                // `data` is assembled field-by-field at the call site — never spread
                // form state here, or UI-only keys trip forbidNonWhitelisted.
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Guarantor', 'Investigation'],
        }),

        // List guarantors for an investigation (employer / agent / admin).
        getInvestigationGuarantors: builder.query<InvestigationGuarantor[], string>({
            query: (id) => `/investigations/${id}/guarantors`,
            providesTags: (_result, _error, id) => [{ type: 'Guarantor', id }],
        }),

        // Agents whose `city` matches a guarantor's town — candidates for reassignment.
        getAvailableAgentsForGuarantor: builder.query<AvailableAgent[], { id: string; guarantorId: string }>({
            query: ({ id, guarantorId }) => `/investigations/${id}/guarantors/${guarantorId}/available-agents`,
        }),

        // Reassign a guarantor to an in-town agent (assigned agent or super-admin).
        reassignGuarantor: builder.mutation<
            InvestigationGuarantor,
            { id: string; guarantorId: string; agentId: string }
        >({
            query: ({ id, guarantorId, agentId }) => ({
                url: `/investigations/${id}/guarantors/${guarantorId}/reassign`,
                method: 'PATCH',
                body: { agentId },
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Guarantor', id }],
        }),

        // Update verification status / notes (the assigned agent or admin).
        updateGuarantorVerification: builder.mutation<
            InvestigationGuarantor,
            { id: string; guarantorId: string; data: UpdateGuarantorVerificationRequest }
        >({
            query: ({ id, guarantorId, data }) => ({
                url: `/investigations/${id}/guarantors/${guarantorId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Guarantor', id }],
        }),

        getInvestigationStats: builder.query<InvestigationStats, void>({
            query: () => '/investigations/stats',
            providesTags: ['Analytics'],
        }),

        // SLA / timer
        getInvestigationTimer: builder.query<InvestigationTimer, string>({
            query: (id) => `/investigations/${id}/timer`,
            transformResponse: (response: any) => response.data || response,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        getSlaSummary: builder.query<SlaSummary, void>({
            query: () => '/investigations/sla/summary',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['Analytics'],
        }),

        assignAgent: builder.mutation<Investigation, { id: string; agentId: string }>({
            query: ({ id, agentId }) => ({
                url: `/admin/investigations/${id}/assign`,
                method: 'PATCH',
                body: { agentId },
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Admin', 'Investigation'],
        }),

        startInvestigation: builder.mutation<Investigation, string>({
            query: (id) => ({
                url: `/investigations/${id}/start`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [{ type: 'Investigation', id }, 'Investigation'],
        }),

        submitReport: builder.mutation<Investigation, { id: string; data: SubmitReportRequest }>({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/submit-report`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Investigation', 'Analytics'],
        }),

        // Reviewer (Super Admin / Customer Success) partial update of a submitted
        // report. Backend enforces those roles and the UNDER_REVIEW state guard;
        // this endpoint does NOT reset the SLA clock or change status — it's for
        // content tweaks during review.
        updateInvestigationReport: builder.mutation<Investigation, { id: string; data: Partial<SubmitReportRequest> }>({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/report`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Investigation'],
        }),

        validateInvestigationReport: builder.mutation<
            Investigation,
            { id: string; approved: boolean; validationNotes?: string }
        >({
            query: ({ id, approved, validationNotes }) => ({
                url: `/investigations/${id}/validate-report`,
                method: 'POST',
                body: { approved, validationNotes },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Investigation',
                'Earning',
                'Analytics',
            ],
        }),

        uploadInvestigationDocument: builder.mutation<
            InvestigationDocument,
            { id: string; file: File; documentType: DocumentType }
        >({
            query: ({ id, file, documentType }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('documentType', documentType);
                return {
                    url: `/investigations/${id}/documents/upload`,
                    method: 'POST',
                    body: formData,
                };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        getInvestigationDocuments: builder.query<InvestigationDocument[], string>({
            query: (id) => `/investigations/${id}/documents`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        validateRequiredDocuments: builder.query<DocumentValidationResponse, string>({
            query: (id) => `/investigations/${id}/documents/validate`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        deleteDocument: builder.mutation<
            { success: boolean; message: string },
            { investigationId: string; documentId: string }
        >({
            query: ({ investigationId, documentId }) => ({
                url: `/investigations/${investigationId}/documents/${documentId}/delete`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { investigationId }) => [{ type: 'Investigation', id: investigationId }],
        }),

        getScoringCriteria: builder.query<ScoringCriteriaResponse, void>({
            query: () => '/investigations/scoring-criteria',
        }),

        submitRiskAssessment: builder.mutation<RiskAssessmentResponse, { id: string; data: RiskAssessmentSubmission }>({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/risk-assessment`,
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: any) => {
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return response;
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'RiskAssessment'],
        }),

        getRiskAssessment: builder.query<RiskAssessmentResponse | null, string>({
            query: (id) => `/investigations/${id}/risk-assessment`,
            transformResponse: (response: any) => {
                // baseQuery unwraps outer { success, data } → inner may still be { success, data: {...} }
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return response ?? null;
            },
            providesTags: (_result, _error, id) => [{ type: 'RiskAssessment', id }],
        }),

        getRiskAssessmentHistory: builder.query<RiskAssessmentResponse[], string>({
            query: (id) => `/investigations/${id}/risk-assessment/history`,
            transformResponse: (response: any) => {
                // baseQuery unwraps outer { success, data } → inner may still be { success, data: [...] }
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return Array.isArray(response) ? response : [];
            },
            providesTags: (_result, _error, id) => [{ type: 'RiskAssessment', id }],
        }),

        uploadAgentDocument: builder.mutation<InvestigationDocument, { id: string; file: File; documentType: string }>({
            query: ({ id, file, documentType }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('documentType', documentType);
                return {
                    url: `/investigations/${id}/agent-documents/upload`,
                    method: 'POST',
                    body: formData,
                };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        uploadMultipleAgentDocuments: builder.mutation<
            InvestigationDocument[],
            { id: string; files: File[]; documentTypes: string[] }
        >({
            query: ({ id, files, documentTypes }) => {
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));
                formData.append('documentTypes', documentTypes.join(','));
                return {
                    url: `/investigations/${id}/agent-documents/upload-multiple`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        approveDocument: builder.mutation<
            InvestigationDocument,
            { investigationId: string; documentId: string; data: DocumentApprovalRequest }
        >({
            query: ({ investigationId, documentId, data }) => ({
                url: `/investigations/${investigationId}/documents/${documentId}/approval`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { investigationId }) => [{ type: 'Investigation', id: investigationId }],
        }),

        getFilteredDocuments: builder.query<InvestigationDocument[], { id: string; uploadedBy?: string }>({
            query: ({ id, uploadedBy }) => ({
                url: `/investigations/${id}/documents`,
                params: uploadedBy ? { uploadedBy } : {},
            }),
            providesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // ==================== PAYMENTS ====================
        getCheckOptions: builder.query<CheckOption[], void>({
            query: () => '/payments/check-options',
            // /payments/check-options ships a *double*-wrapped envelope —
            // { success, data: { success, data: [...] } }. baseQuery strips the
            // outer wrapper; we strip the inner one here. Without this the value
            // is the inner object, `.length` is undefined, and the modal falls
            // back to FALLBACK_OPTIONS — silently hiding referral discounts.
            transformResponse: (response: any) => {
                if (Array.isArray(response)) return response as CheckOption[];
                if (response && Array.isArray(response.data)) return response.data as CheckOption[];
                return [];
            },
            providesTags: ['CheckOptions'],
        }),

        // Gateway-agnostic payment endpoints. The backend routes these to the
        // primary gateway (currently Monnify); switching gateways needs no
        // frontend change. Force-Monnify exists at /payments/monnify/* if needed.
        initiatePayment: builder.mutation<InitiatePaymentResponse, InitiatePaymentRequest>({
            query: (data) => ({
                url: '/payments/initiate',
                method: 'POST',
                body: data,
            }),
            // A FREE promo settles the investigation here (no gateway redirect), so
            // bust the lists/analytics immediately. Normal/percentage flows redirect
            // away, so there's nothing to invalidate in-page.
            invalidatesTags: (result) => (result?.free ? ['Investigation', 'Transaction', 'Analytics'] : []),
        }),

        verifyPayment: builder.mutation<VerifyPaymentResponse, VerifyPaymentRequest>({
            query: ({ tx_ref }) => ({
                url: '/payments/verify',
                method: 'GET',
                params: { tx_ref },
            }),
            invalidatesTags: ['Investigation', 'Transaction'],
        }),

        getMyTransactions: builder.query<Transaction[], void>({
            query: () => '/payments/transactions',
            providesTags: ['Transaction'],
        }),

        getTransactionById: builder.query<Transaction, string>({
            query: (id) => `/payments/transactions/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Transaction', id }],
        }),

        getReusablePayments: builder.query<ReusablePayment[], void>({
            query: () => '/payments/reusable-payments',
            providesTags: ['Transaction'],
        }),

        // ==================== EARNINGS (AGENT) ====================
        getMyEarnings: builder.query<Earning[], EarningStatus | undefined>({
            query: (status) => ({
                url: '/earnings/my-earnings',
                params: status ? { status } : {},
            }),
            providesTags: ['Earning'],
        }),

        getMyEarningsSummary: builder.query<EarningsSummary, void>({
            query: () => '/earnings/my-summary',
            providesTags: ['Earning'],
        }),

        getEarningsBreakdown: builder.query<EarningsBreakdown, void>({
            query: () => '/analytics/earnings/breakdown',
            providesTags: ['Earning', 'Analytics'],
        }),

        // ==================== ANALYTICS ====================
        getDashboardStats: builder.query<DashboardStats, { startDate?: string; endDate?: string } | void>({
            query: (params) => ({
                url: '/analytics/dashboard',
                params: params || {},
            }),
            providesTags: ['Analytics'],
        }),

        getInvestigationTrends: builder.query<InvestigationTrend[], { startDate?: string }>({
            query: (params) => ({
                url: '/analytics/investigations/trends',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        getRevenueTrends: builder.query<RevenueTrend[], { startDate?: string }>({
            query: (params) => ({
                url: '/analytics/revenue/trends',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        getTopAgents: builder.query<TopAgent[], { limit?: number }>({
            query: (params) => ({
                url: '/analytics/agents/top',
                params,
            }),
            providesTags: ['Analytics', 'Admin'],
        }),

        getStatusDistribution: builder.query<StatusDistribution[], void>({
            query: () => '/analytics/investigations/status-distribution',
            providesTags: ['Analytics'],
        }),

        getFinancialStats: builder.query<FinancialStats, { startDate?: string; endDate?: string }>({
            query: (params) => ({
                url: '/analytics/financial/stats',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        // ==================== ADMIN (SUPER_ADMIN) ====================
        inviteAgent: builder.mutation<User, InviteAgentRequest>({
            query: (data) => ({
                url: '/admin/agents/invite',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin', 'User'],
        }),

        // Create an agent / customer_success / medical / hr staff user. Account is
        // created active + verified; an invitation notification is sent.
        createStaff: builder.mutation<User, CreateStaffRequest>({
            query: (data) => ({
                url: '/admin/users',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin', 'User'],
        }),

        getAllUsers: builder.query<User[], { role?: string; status?: string }>({
            query: (params) => ({
                url: '/admin/users',
                params,
            }),
            providesTags: ['Admin', 'User'],
        }),

        getUserById: builder.query<UserWithStats, string>({
            query: (id) => `/admin/users/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'User', id }],
        }),

        updateUserStatus: builder.mutation<User, { id: string; data: UpdateUserStatusRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/users/${id}/status`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Admin', 'User'],
        }),

        // Flag an employer account as payment-exempt (complimentary) or back to normal.
        // Only valid on employer accounts (400 otherwise). Returns the updated user.
        setPaymentExemption: builder.mutation<User, { id: string; data: PaymentExemptionRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/users/${id}/payment-exemption`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Admin', 'User'],
        }),

        deleteUser: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: `/admin/users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Admin', 'User'],
        }),

        getAllInvestigations: builder.query<Investigation[], AdminInvestigationFilters | void>({
            query: (params) => ({
                url: '/admin/investigations',
                params: params || {},
            }),
            providesTags: ['Admin', 'Investigation'],
        }),

        reassignInvestigation: builder.mutation<Investigation, { id: string; data: ReassignInvestigationRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/investigations/${id}/reassign`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Admin', 'Investigation'],
        }),

        // Assign / change the Customer Success owner of an investigation.
        adminAssignCustomerSuccess: builder.mutation<Investigation, { id: string; customerSuccessId: string }>({
            query: ({ id, customerSuccessId }) => ({
                url: `/admin/investigations/${id}/assign-cs`,
                method: 'PATCH',
                body: { customerSuccessId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Admin',
                'Investigation',
                'CustomerSuccess',
            ],
        }),

        // Assign / change the Medical user of an investigation.
        adminAssignMedicalUser: builder.mutation<Investigation, { id: string; medicalUserId: string }>({
            query: ({ id, medicalUserId }) => ({
                url: `/admin/investigations/${id}/assign-medical`,
                method: 'PATCH',
                body: { medicalUserId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Admin',
                'Investigation',
                'Medical',
            ],
        }),

        // Assign / change the Notary of an investigation.
        adminAssignNotary: builder.mutation<Investigation, { id: string; notaryUserId: string }>({
            query: ({ id, notaryUserId }) => ({
                url: `/admin/investigations/${id}/assign-notary`,
                method: 'PATCH',
                body: { notaryUserId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Admin',
                'Investigation',
                'Notary',
            ],
        }),

        // Assign / change the Police user of an investigation.
        adminAssignPolice: builder.mutation<Investigation, { id: string; policeUserId: string }>({
            query: ({ id, policeUserId }) => ({
                url: `/admin/investigations/${id}/assign-police`,
                method: 'PATCH',
                body: { policeUserId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Admin',
                'Investigation',
                'Police',
            ],
        }),

        // Admin-editable pricing config (plan prices, medical add-on, earnings splits).
        getPricing: builder.query<PricingConfig, void>({
            query: () => '/admin/pricing',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Pricing'],
        }),

        // Send any subset of the numeric fields; saved + re-applied immediately.
        // Bust CheckOptions so the pricing modal refetches the new plan prices.
        updatePricing: builder.mutation<PricingConfig, UpdatePricingRequest>({
            query: (data) => ({
                url: '/admin/pricing',
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Pricing', 'CheckOptions'],
        }),

        // Admin-editable plan content (name, popular badge, feature bullets, order, visibility).
        getAdminPlans: builder.query<AdminPlan[], void>({
            query: () => '/admin/plans',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Plan'],
        }),

        // Partial update; `features` (when sent) replaces the whole list. Busts
        // CheckOptions so the pricing cards reflect the change immediately.
        updatePlan: builder.mutation<AdminPlan, { id: string; data: UpdatePlanRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/plans/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Plan', 'CheckOptions', 'Pricing'],
        }),

        // ==================== ADMIN: PRICED FEATURE CATALOG ====================
        // The lines a plan is built from. A plan's price is the sum of its
        // features, so this is where prices are actually set. Re-pricing a
        // feature re-prices every plan that includes it, so these all bust
        // CheckOptions and Pricing as well.
        getPlanFeatures: builder.query<AdminPlanFeature[], void>({
            query: () => '/admin/plan-features',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['PlanFeature'],
        }),

        createPlanFeature: builder.mutation<AdminPlanFeature, CreatePlanFeatureRequest>({
            query: (body) => ({ url: '/admin/plan-features', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['PlanFeature', 'Plan', 'Pricing', 'CheckOptions'],
        }),

        updatePlanFeature: builder.mutation<AdminPlanFeature, { id: string; data: UpdatePlanFeatureRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/plan-features/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['PlanFeature', 'Plan', 'Pricing', 'CheckOptions'],
        }),

        // Refused by the API while any plan still includes the feature.
        deletePlanFeature: builder.mutation<{ id: string; deleted: true }, string>({
            query: (id) => ({ url: `/admin/plan-features/${id}`, method: 'DELETE' }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['PlanFeature', 'Plan', 'Pricing', 'CheckOptions'],
        }),

        // ==================== ADMIN: CHECK ADD-ON CATALOG ====================
        // The à la carte extras sold on top of a plan. Fully admin-owned:
        // create, re-price, choose which tiers sell them, reorder, hide, delete.
        // Every mutation busts CheckOptions so the pricing cards update at once.
        getCheckAddons: builder.query<AdminCheckAddon[], void>({
            query: () => '/admin/check-addons',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['CheckAddon'],
        }),

        createCheckAddon: builder.mutation<AdminCheckAddon, CreateCheckAddonRequest>({
            query: (body) => ({ url: '/admin/check-addons', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['CheckAddon', 'CheckOptions'],
        }),

        updateCheckAddon: builder.mutation<AdminCheckAddon, { id: string; data: UpdateCheckAddonRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/check-addons/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['CheckAddon', 'CheckOptions'],
        }),

        // Refused by the API once an investigation has been charged for the
        // add-on — hide it (isActive: false) instead of deleting.
        deleteCheckAddon: builder.mutation<{ id: string; deleted: true }, string>({
            query: (id) => ({ url: `/admin/check-addons/${id}`, method: 'DELETE' }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['CheckAddon', 'CheckOptions'],
        }),

        // Send every id in the order you want them displayed.
        reorderCheckAddons: builder.mutation<AdminCheckAddon[], string[]>({
            query: (ids) => ({ url: '/admin/check-addons/reorder', method: 'PATCH', body: { ids } }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['CheckAddon', 'CheckOptions'],
        }),

        // Restore the built-in add-ons to their default name, price and tiers.
        reseedCheckAddons: builder.mutation<AdminCheckAddon[], void>({
            query: () => ({ url: '/admin/check-addons/reseed', method: 'POST' }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['CheckAddon', 'CheckOptions'],
        }),

        getPlatformStatistics: builder.query<PlatformStatistics, void>({
            query: () => '/admin/statistics',
            providesTags: ['Admin', 'Analytics'],
        }),

        getAllEarnings: builder.query<Earning[], { status?: EarningStatus }>({
            query: (params) => ({
                url: '/earnings',
                params,
            }),
            providesTags: ['Admin', 'Earning'],
        }),

        getPlatformEarningsStats: builder.query<PlatformEarningsStats, void>({
            query: () => '/earnings/platform-stats',
            providesTags: ['Admin', 'Earning', 'Analytics'],
        }),

        markEarningAsPaid: builder.mutation<MarkEarningPaidResponse, string>({
            query: (id) => ({
                url: `/earnings/${id}/mark-paid`,
                method: 'PATCH',
            }),
            invalidatesTags: ['Earning', 'Admin'],
        }),

        bulkMarkEarningsAsPaid: builder.mutation<BulkMarkPaidResponse, BulkMarkPaidRequest>({
            query: (data) => ({
                url: '/earnings/bulk/mark-paid',
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Earning', 'Admin'],
        }),

        // ==================== CALLBACK REQUESTS ====================
        // Backend endpoint is still to be built — this is the contract the
        // /intelligence one-pager posts to. Until it lands the request 404s and
        // the form surfaces a retry, so no lead is silently swallowed.
        requestCallback: builder.mutation<CallbackRequestResponse, CallbackRequestPayload>({
            query: (data) => ({
                url: '/callbacks',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Callback'],
        }),

        // Admin queue (super_admin + customer_success). The API rejects unknown
        // query params outright, so only status/page/limit are ever sent — and
        // page/limit must be integers, never '' or 'all'.
        getCallbacks: builder.query<Paginated<Callback>, CallbackListParams | void>({
            query: (args) => {
                const { status, page = 1, limit = 20 } = args ?? {};
                const params: Record<string, string | number> = {
                    page: Math.max(1, Math.trunc(page)),
                    limit: Math.min(100, Math.max(1, Math.trunc(limit))),
                };
                if (status) params.status = status;
                return { url: '/callbacks', params };
            },
            transformResponse: (response: any): Paginated<Callback> => {
                const data = response?.data ?? response;
                return {
                    items: Array.isArray(data?.items) ? data.items : [],
                    total: data?.total ?? 0,
                    page: data?.page ?? 1,
                    limit: data?.limit ?? 20,
                    totalPages: data?.totalPages ?? 1,
                };
            },
            providesTags: ['Callback'],
        }),

        updateCallbackStatus: builder.mutation<Callback, { id: string; status: CallbackStatus }>({
            query: ({ id, status }) => ({
                url: `/callbacks/${id}`,
                method: 'PATCH',
                // The write surface is exactly this one field — no notes, no
                // assignment, no bulk update.
                body: { status },
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Callback'],
        }),

        // ==================== WAITLIST ====================
        joinWaitlist: builder.mutation<WaitlistResponse, WaitlistEntry>({
            query: (data) => ({
                url: '/waitlist',
                method: 'POST',
                body: data,
            }),
        }),

        getAllWaitlistEntries: builder.query<WaitlistResponse[], { status?: string }>({
            query: (params) => ({
                url: '/waitlist',
                params,
            }),
            providesTags: ['Waitlist'],
        }),

        getWaitlistStats: builder.query<WaitlistStats, void>({
            query: () => '/waitlist/stats',
            providesTags: ['Waitlist'],
        }),

        checkEmailOnWaitlist: builder.query<CheckEmailResponse, string>({
            query: (email) => `/waitlist/check/${email}`,
        }),

        getWaitlistEntryById: builder.query<WaitlistResponse, number>({
            query: (id) => `/waitlist/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Waitlist', id }],
        }),

        updateWaitlistEntry: builder.mutation<WaitlistResponse, { id: number; data: UpdateWaitlistRequest }>({
            query: ({ id, data }) => ({
                url: `/waitlist/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Waitlist', id }, 'Waitlist'],
        }),

        bulkUpdateWaitlistStatus: builder.mutation<WaitlistResponse[], BulkUpdateWaitlistRequest>({
            query: (data) => ({
                url: '/waitlist/bulk/status',
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Waitlist'],
        }),

        deleteWaitlistEntry: builder.mutation<void, number>({
            query: (id) => ({
                url: `/waitlist/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Waitlist'],
        }),

        // ==================== SUPPORT TICKETS ====================
        // User endpoints
        createSupportTicket: builder.mutation<SupportTicket, CreateTicketRequest>({
            query: (data) => ({
                url: '/support/tickets',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SupportTicket'],
        }),

        getMyTickets: builder.query<SupportTicket[], void>({
            query: () => '/support/tickets/my-tickets',
            providesTags: ['SupportTicket'],
        }),

        getMyTicketById: builder.query<SupportTicket, string>({
            query: (id) => `/support/tickets/my-tickets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'SupportTicket', id }],
        }),

        // Admin endpoints
        getAllTickets: builder.query<
            SupportTicket[],
            { status?: TicketStatus; priority?: TicketPriority; category?: TicketCategory }
        >({
            query: (params) => ({
                url: '/support/admin/tickets',
                params,
            }),
            providesTags: ['SupportTicket'],
        }),

        getTicketById: builder.query<SupportTicket, string>({
            query: (id) => `/support/admin/tickets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'SupportTicket', id }],
        }),

        updateTicketStatus: builder.mutation<SupportTicket, { id: string; data: UpdateTicketStatusRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/status`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        updateTicketPriority: builder.mutation<SupportTicket, { id: string; data: UpdateTicketPriorityRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/priority`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        assignTicket: builder.mutation<SupportTicket, { id: string; data: AssignTicketRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/assign`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        getTicketStatistics: builder.query<TicketStatistics, void>({
            query: () => '/support/admin/statistics',
            providesTags: ['SupportTicket'],
        }),

        // ==================== EMPLOYEE SUPPORT (no auth, supportCode-based) ====================

        openEmployeeTicket: builder.mutation<SupportTicket, EmployeeOpenTicketRequest>({
            query: (body) => ({
                url: '/support/employee/tickets',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SupportTicket'],
        }),

        listEmployeeTickets: builder.query<SupportTicket[], { supportCode: string }>({
            query: ({ supportCode }) => ({
                url: '/support/employee/tickets',
                params: { supportCode },
            }),
            providesTags: ['SupportTicket'],
        }),

        getEmployeeTicket: builder.query<SupportTicket, { id: string; supportCode: string }>({
            query: ({ id, supportCode }) => ({
                url: `/support/employee/tickets/${id}`,
                params: { supportCode },
            }),
            providesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }],
        }),

        replyEmployeeTicket: builder.mutation<SupportTicket, { id: string; data: EmployeeReplyRequest }>({
            query: ({ id, data }) => ({
                url: `/support/employee/tickets/${id}/reply`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        // ==================== ADMIN SUPPORT REPLY (NEW) ====================

        replyAdminTicket: builder.mutation<SupportTicket, { id: string; data: AdminReplyRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/reply`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        // ==================== EMPLOYER SUPPORT (auth, ownership-scoped) ====================
        // Lands on the same thread the employee sees via support code, and the
        // same thread super-admins see in /admin/support. Messages are tagged
        // senderType=employer for distinct rendering.

        getEmployerTickets: builder.query<SupportTicket[], { investigationId?: string } | void>({
            query: (args) => {
                const url = '/support/employer/tickets';
                if (args && args.investigationId) {
                    return { url, params: { investigationId: args.investigationId } };
                }
                return { url };
            },
            providesTags: ['SupportTicket'],
        }),

        getEmployerTicket: builder.query<SupportTicket, string>({
            query: (id) => `/support/employer/tickets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'SupportTicket', id }],
        }),

        openEmployerTicket: builder.mutation<SupportTicket, OpenEmployerTicketRequest>({
            query: (body) => ({
                url: '/support/employer/tickets',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SupportTicket'],
        }),

        replyEmployerTicket: builder.mutation<SupportTicket, { id: string; data: EmployerReplyRequest }>({
            query: ({ id, data }) => ({
                url: `/support/employer/tickets/${id}/reply`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        // ==================== BANK ACCOUNTS ====================
        // User endpoints
        addBankAccount: builder.mutation<BankAccount, AddBankAccountRequest>({
            query: (data) => ({
                url: '/bank-accounts',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['BankAccount'],
        }),

        getMyBankAccounts: builder.query<BankAccount[], void>({
            query: () => '/bank-accounts/my-accounts',
            providesTags: ['BankAccount'],
        }),

        getMyBankAccountById: builder.query<BankAccount, string>({
            query: (id) => `/bank-accounts/my-accounts/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'BankAccount', id }],
        }),

        updateBankAccount: builder.mutation<BankAccount, { id: string; data: UpdateBankAccountRequest }>({
            query: ({ id, data }) => ({
                url: `/bank-accounts/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'BankAccount', id }, 'BankAccount'],
        }),

        setDefaultBankAccount: builder.mutation<BankAccount, string>({
            query: (id) => ({
                url: `/bank-accounts/${id}/set-default`,
                method: 'PATCH',
            }),
            invalidatesTags: ['BankAccount'],
        }),

        deleteBankAccount: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: `/bank-accounts/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['BankAccount'],
        }),

        // Admin endpoints
        getAllBankAccounts: builder.query<BankAccount[], void>({
            query: () => '/bank-accounts/admin/all',
            providesTags: ['BankAccount'],
        }),

        // ==================== WITHDRAWALS ====================
        // User endpoints
        createWithdrawal: builder.mutation<Withdrawal, CreateWithdrawalRequest>({
            query: (data) => ({
                url: '/withdrawals',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Withdrawal'],
        }),

        getMyWithdrawals: builder.query<Withdrawal[], void>({
            query: () => '/withdrawals/my-withdrawals',
            providesTags: ['Withdrawal'],
        }),

        getMyWithdrawalById: builder.query<Withdrawal, string>({
            query: (id) => `/withdrawals/my-withdrawals/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Withdrawal', id }],
        }),

        // Admin endpoints
        getAllWithdrawals: builder.query<Withdrawal[], { status?: WithdrawalStatus }>({
            query: (params) => ({
                url: '/withdrawals/admin/all',
                params,
            }),
            providesTags: ['Withdrawal'],
        }),

        getWithdrawalById: builder.query<Withdrawal, string>({
            query: (id) => `/withdrawals/admin/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Withdrawal', id }],
        }),

        reviewWithdrawal: builder.mutation<Withdrawal, { id: string; data: ReviewWithdrawalRequest }>({
            query: ({ id, data }) => ({
                url: `/withdrawals/admin/${id}/review`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Withdrawal', id }, 'Withdrawal'],
        }),

        processWithdrawal: builder.mutation<Withdrawal, { id: string; data: ProcessWithdrawalRequest }>({
            query: ({ id, data }) => ({
                url: `/withdrawals/admin/${id}/process`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Withdrawal', id }, 'Withdrawal'],
        }),

        getWithdrawalStatistics: builder.query<WithdrawalStatistics, void>({
            query: () => '/withdrawals/admin/statistics/summary',
            providesTags: ['Withdrawal'],
        }),

        // ==================== HR REFERRAL PROGRAM ====================
        // Lazy-generates the HR's referral code on first call.
        getHrMe: builder.query<HrMeResponse, void>({
            query: () => '/hr/me',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['HrMe'],
        }),

        getHrReferrals: builder.query<HrReferral[], void>({
            query: () => '/hr/referrals',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['HrReferral'],
        }),

        getHrEarnings: builder.query<HrEarning[], { status?: HrEarningStatus; employerId?: string } | void>({
            query: (args) => {
                const params: Record<string, string> = {};
                if (args?.status) params.status = args.status;
                if (args?.employerId) params.employerId = args.employerId;
                return { url: '/hr/earnings', params };
            },
            transformResponse: (response: any) => response.data || response,
            providesTags: ['HrEarning'],
        }),

        getHrEarningsSummary: builder.query<HrEarningsSummary, void>({
            query: () => '/hr/earnings/summary',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['HrEarning'],
        }),

        getHrLeaderboard: builder.query<HrLeaderboardEntry[], void>({
            query: () => '/hr/leaderboard',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['HrLeaderboard'],
        }),

        // ==================== TRAINING — AGENT ====================
        getTrainingCourses: builder.query<TrainingCourseSummary[], void>({
            query: () => '/training/courses',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Training'],
        }),

        getTrainingCourse: builder.query<TrainingCourseDetail, string>({
            query: (id) => `/training/courses/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_res, _err, id) => [{ type: 'Training', id }],
        }),

        enrollTrainingCourse: builder.mutation<EnrollCourseResponse, string>({
            query: (id) => ({ url: `/training/courses/${id}/enroll`, method: 'POST' }),
            invalidatesTags: (_res, _err, id) => [{ type: 'Training', id }, 'Training'],
        }),

        markVideoWatched: builder.mutation<MarkWatchedResponse, { videoId: string; courseId?: string }>({
            query: ({ videoId }) => ({ url: `/training/videos/${videoId}/watch`, method: 'POST' }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId
                    ? [{ type: 'Training', id: courseId }, 'Training', 'Certificate']
                    : ['Training', 'Certificate'],
        }),

        // Quiz answers are fetched on demand and never cached, so the correct
        // answer is re-requested fresh on every retake.
        getVideoQuiz: builder.query<VideoQuiz, string>({
            query: (videoId) => `/training/videos/${videoId}/quiz`,
            transformResponse: (response: any) => response?.data ?? response,
            keepUnusedDataFor: 0,
        }),

        submitQuiz: builder.mutation<SubmitQuizResponse, { videoId: string; courseId?: string; answers: QuizAnswer[] }>(
            {
                query: ({ videoId, answers }) => ({
                    url: `/training/videos/${videoId}/quiz/submit`,
                    method: 'POST',
                    body: { answers },
                }),
                invalidatesTags: (_res, _err, { courseId }) =>
                    courseId
                        ? [{ type: 'Training', id: courseId }, 'Training', 'Certificate']
                        : ['Training', 'Certificate'],
            },
        ),

        getMyCertificates: builder.query<TrainingCertificate[], void>({
            query: () => '/training/certificates',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Certificate'],
        }),

        getCourseCertificate: builder.query<TrainingCertificate, string>({
            query: (id) => `/training/courses/${id}/certificate`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Certificate'],
        }),

        // ==================== TRAINING — ADMIN (super_admin) ====================
        adminCreateCourse: builder.mutation<AdminTrainingCourse, CreateCourseRequest>({
            query: (body) => ({ url: '/training/admin/courses', method: 'POST', body }),
            invalidatesTags: ['Training'],
        }),

        adminListCourses: builder.query<AdminTrainingCourse[], void>({
            query: () => '/training/admin/courses',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Training'],
        }),

        adminGetCourse: builder.query<AdminTrainingCourse, string>({
            query: (id) => `/training/admin/courses/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_res, _err, id) => [{ type: 'Training', id }],
        }),

        adminUpdateCourse: builder.mutation<AdminTrainingCourse, { id: string; body: UpdateCourseRequest }>({
            query: ({ id, body }) => ({ url: `/training/admin/courses/${id}`, method: 'PATCH', body }),
            invalidatesTags: (_res, _err, { id }) => [{ type: 'Training', id }, 'Training'],
        }),

        adminDeleteCourse: builder.mutation<DeletedResponse, string>({
            query: (id) => ({ url: `/training/admin/courses/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Training'],
        }),

        adminAddVideoLink: builder.mutation<AdminTrainingVideo, { courseId: string; body: AddVideoLinkRequest }>({
            query: ({ courseId, body }) => ({
                url: `/training/admin/courses/${courseId}/videos`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, { courseId }) => [{ type: 'Training', id: courseId }, 'Training'],
        }),

        adminUploadVideo: builder.mutation<AdminTrainingVideo, { courseId: string; formData: FormData }>({
            query: ({ courseId, formData }) => ({
                url: `/training/admin/courses/${courseId}/videos/upload`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: (_res, _err, { courseId }) => [{ type: 'Training', id: courseId }, 'Training'],
        }),

        adminUpdateVideo: builder.mutation<
            AdminTrainingVideo,
            { id: string; courseId?: string; body: UpdateVideoRequest }
        >({
            query: ({ id, body }) => ({ url: `/training/admin/videos/${id}`, method: 'PATCH', body }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId ? [{ type: 'Training', id: courseId }, 'Training'] : ['Training'],
        }),

        adminDeleteVideo: builder.mutation<DeletedResponse, { id: string; courseId?: string }>({
            query: ({ id }) => ({ url: `/training/admin/videos/${id}`, method: 'DELETE' }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId ? [{ type: 'Training', id: courseId }, 'Training'] : ['Training'],
        }),

        adminAddQuestion: builder.mutation<
            AdminTrainingQuestion,
            { videoId: string; courseId?: string; body: AddQuestionRequest }
        >({
            query: ({ videoId, body }) => ({
                url: `/training/admin/videos/${videoId}/questions`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId ? [{ type: 'Training', id: courseId }, 'Training'] : ['Training'],
        }),

        adminUpdateQuestion: builder.mutation<
            AdminTrainingQuestion,
            { id: string; courseId?: string; body: UpdateQuestionRequest }
        >({
            query: ({ id, body }) => ({ url: `/training/admin/questions/${id}`, method: 'PATCH', body }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId ? [{ type: 'Training', id: courseId }, 'Training'] : ['Training'],
        }),

        adminDeleteQuestion: builder.mutation<DeletedResponse, { id: string; courseId?: string }>({
            query: ({ id }) => ({ url: `/training/admin/questions/${id}`, method: 'DELETE' }),
            invalidatesTags: (_res, _err, { courseId }) =>
                courseId ? [{ type: 'Training', id: courseId }, 'Training'] : ['Training'],
        }),

        // ==================== CONTACT MESSAGES ====================
        // Public: landing-page visitors send the admin a message (no auth required).
        submitContactMessage: builder.mutation<ContactMessage, SubmitContactMessageRequest>({
            query: (body) => ({ url: '/contact-messages', method: 'POST', body }),
            invalidatesTags: ['ContactMessage'],
        }),

        // Admin inbox.
        getContactMessages: builder.query<ContactMessage[], void>({
            query: () => '/admin/contact-messages',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['ContactMessage'],
        }),

        updateContactMessageStatus: builder.mutation<ContactMessage, UpdateContactMessageStatusRequest>({
            query: ({ id, status }) => ({ url: `/admin/contact-messages/${id}`, method: 'PATCH', body: { status } }),
            invalidatesTags: ['ContactMessage'],
        }),

        deleteContactMessage: builder.mutation<DeletedResponse, string>({
            query: (id) => ({ url: `/admin/contact-messages/${id}`, method: 'DELETE' }),
            invalidatesTags: ['ContactMessage'],
        }),

        // ==================== CUSTOMER SUCCESS ====================
        // Role: customer_success (the assigned CS) or super_admin. Every
        // investigation is auto-assigned round-robin to the least-loaded CS.
        getCsMetrics: builder.query<CsMetrics, void>({
            query: () => '/customer-success/metrics',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['CustomerSuccess'],
        }),

        getCsInvestigations: builder.query<Investigation[], void>({
            query: () => '/customer-success/investigations',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['CustomerSuccess'],
        }),

        getCsInvestigation: builder.query<Investigation, string>({
            query: (id) => `/customer-success/investigations/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [
                { type: 'CustomerSuccess', id },
                { type: 'Investigation', id },
            ],
        }),

        // CS progress checklist — GET returns sections + progress; owner CS or Super Admin.
        getCsChecklist: builder.query<CsChecklist, string>({
            query: (id) => `/customer-success/investigations/${id}/checklist`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'CsChecklist', id }],
        }),

        // Toggle one or many steps. Returns the full refreshed checklist, which the
        // caller uses directly; the tag invalidation keeps a Super Admin's GET fresh.
        updateCsChecklist: builder.mutation<CsChecklist, UpdateCsChecklistRequest>({
            query: ({ id, sections }) => ({
                url: `/customer-success/investigations/${id}/checklist`,
                method: 'PATCH',
                body: { sections },
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'CsChecklist', id }],
        }),

        // Delegates to the core assignment flow (payment + consent checks, SLA timers).
        csAssignAgent: builder.mutation<Investigation, { id: string; agentId: string }>({
            query: ({ id, agentId }) => ({
                url: `/customer-success/investigations/${id}/assign-agent`,
                method: 'POST',
                body: { agentId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'CustomerSuccess', id },
                { type: 'Investigation', id },
                'CustomerSuccess',
            ],
        }),

        // Assign a `medical` user. 404 if the target isn't a medical user.
        csAssignMedical: builder.mutation<AssignMedicalResponse, { id: string; medicalUserId: string }>({
            query: ({ id, medicalUserId }) => ({
                url: `/customer-success/investigations/${id}/assign-medical`,
                method: 'POST',
                body: { medicalUserId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'CustomerSuccess', id },
                { type: 'Investigation', id },
                'CustomerSuccess',
            ],
        }),

        // WhatsApp + email to the employee (test go-ahead, police-capture info).
        csNotifyEmployee: builder.mutation<MessageSentResponse, { id: string; data: NotifyMessageRequest }>({
            query: ({ id, data }) => ({
                url: `/customer-success/investigations/${id}/notify-employee`,
                method: 'POST',
                body: data,
            }),
        }),

        // Email update to the employer.
        csUpdateEmployer: builder.mutation<MessageSentResponse, { id: string; data: NotifyMessageRequest }>({
            query: ({ id, data }) => ({
                url: `/customer-success/investigations/${id}/update-employer`,
                method: 'POST',
                body: data,
            }),
        }),

        // Reassign an out-of-town guarantor to an in-town agent. CS-owned (not the
        // agent). Triggers the earnings split on report approval.
        csReassignGuarantor: builder.mutation<
            InvestigationGuarantor,
            { id: string; guarantorId: string; agentId: string }
        >({
            query: ({ id, guarantorId, agentId }) => ({
                url: `/customer-success/investigations/${id}/guarantors/${guarantorId}/reassign`,
                method: 'PATCH',
                body: { agentId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Guarantor', id },
                { type: 'CustomerSuccess', id },
            ],
        }),

        // Active agents or medical users a CS can assign — CS-accessible, no
        // /admin/users needed. `role` must be 'agent' or 'medical' (else 400).
        getCsAssignableUsers: builder.query<AvailableAgent[], { role: 'agent' | 'medical' }>({
            query: ({ role }) => ({
                url: '/customer-success/assignable-users',
                params: { role },
            }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== MEDICAL ====================
        // Role: medical (assigned), customer_success (owning CS), or super_admin.
        getMedicalMetrics: builder.query<MedicalMetrics, void>({
            query: () => '/medical/metrics',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Medical'],
        }),

        getMedicalInvestigations: builder.query<Investigation[], void>({
            query: () => '/medical/investigations',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Medical'],
        }),

        getMedicalInvestigation: builder.query<Investigation, string>({
            query: (id) => `/medical/investigations/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [
                { type: 'Medical', id },
                { type: 'Investigation', id },
            ],
        }),

        // ==================== NOTARY WORKSPACE ====================
        // The notary's own assignments.
        getNotaryInvestigations: builder.query<NotaryInvestigationListItem[], void>({
            query: () => '/notary/investigations',
            transformResponse: (response: any) => (Array.isArray(response) ? response : (response?.data ?? [])),
            providesTags: ['Notary'],
        }),

        // Privacy-scoped detail (basic info + affidavits only).
        getNotaryInvestigation: builder.query<NotaryInvestigationDetail, string>({
            query: (id) => `/notary/investigations/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'Notary', id }],
        }),

        // Upload the final notarised copy (multipart). Sets notaryCompletedAt and
        // emails the employee. 400 if the employee hasn't submitted their affidavit.
        uploadFinalAffidavit: builder.mutation<NotaryInvestigationDetail, { id: string; file: File }>({
            query: ({ id, file }) => {
                const fd = new FormData();
                fd.append('finalAffidavit', file);
                return { url: `/notary/investigations/${id}/final-affidavit`, method: 'POST', body: fd };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Notary', id },
                'Notary',
                { type: 'Investigation', id },
            ],
        }),

        // ==================== POLICE WORKSPACE ====================
        // The police user's own assignments.
        getPoliceInvestigations: builder.query<PoliceInvestigationListItem[], void>({
            query: () => '/police/investigations',
            transformResponse: (response: any) => (Array.isArray(response) ? response : (response?.data ?? [])),
            providesTags: ['Police'],
        }),

        // Privacy-scoped detail (basic profile + photo + certificate only).
        getPoliceInvestigation: builder.query<PoliceInvestigationDetail, string>({
            query: (id) => `/police/investigations/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'Police', id }],
        }),

        // Upload the police character certificate (multipart). Replaces any prior
        // certificate, sets policeCompletedAt, returns the refreshed detail. 400 if
        // no file, 403 if not assigned.
        uploadPoliceCharacterCertificate: builder.mutation<PoliceInvestigationDetail, { id: string; file: File }>({
            query: ({ id, file }) => {
                const fd = new FormData();
                fd.append('certificate', file);
                return { url: `/police/investigations/${id}/character-certificate`, method: 'POST', body: fd };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Police', id },
                'Police',
                { type: 'Investigation', id },
            ],
        }),

        // Required test panel for the investigation's category.
        getMedicalTests: builder.query<MedicalTestPanel, string>({
            query: (id) => `/medical/investigations/${id}/tests`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'Medical', id }],
        }),

        // The current report, or null if none entered yet.
        getMedicalReport: builder.query<InvestigationMedicalReport | null, string>({
            query: (id) => `/medical/investigations/${id}/report`,
            transformResponse: (response: any) => {
                if (response?.data !== undefined && response?.success !== undefined) return response.data ?? null;
                return response ?? null;
            },
            providesTags: (_result, _error, id) => [{ type: 'Medical', id }],
        }),

        // Upsert the single report per investigation. All tests for the category
        // are required (each key must have a `result`) or the server returns 400.
        submitMedicalReport: builder.mutation<
            InvestigationMedicalReport,
            { id: string; data: SubmitMedicalReportRequest }
        >({
            query: ({ id, data }) => ({
                url: `/medical/investigations/${id}/report`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Medical', id },
                { type: 'Investigation', id },
            ],
        }),

        // ==================== MEDICAL LABS ====================
        // Picker source. Medical/CS only ever see active labs (server-forced);
        // admins may pass includeInactive=true for the management screen.
        getMedicalLabs: builder.query<MedicalLab[], MedicalLabFilters | void>({
            query: (args) => {
                const params: Record<string, string> = {};
                if (args?.state) params.state = args.state;
                if (args?.region) params.region = args.region;
                if (args?.search) params.search = args.search;
                if (args?.includeInactive) params.includeInactive = 'true';
                return { url: '/medical-labs', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['MedicalLab'],
        }),

        getMedicalLab: builder.query<MedicalLab, string>({
            query: (id) => `/medical-labs/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'MedicalLab', id }],
        }),

        // Assign a lab — call whichever route matches the logged-in role. The
        // backend stamps the investigation and emails the employee the lab + tests.
        medicalAssignLab: builder.mutation<AssignLabResponse, { id: string; labId: string }>({
            query: ({ id, labId }) => ({
                url: `/medical/investigations/${id}/assign-lab`,
                method: 'POST',
                body: { labId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Medical', id },
                { type: 'Investigation', id },
            ],
        }),

        csAssignLab: builder.mutation<AssignLabResponse, { id: string; labId: string }>({
            query: ({ id, labId }) => ({
                url: `/customer-success/investigations/${id}/assign-lab`,
                method: 'POST',
                body: { labId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'CustomerSuccess', id },
                { type: 'Investigation', id },
            ],
        }),

        // ---- Admin catalog (super_admin) ----
        createMedicalLab: builder.mutation<MedicalLab, CreateMedicalLabRequest>({
            query: (body) => ({ url: '/medical-labs', method: 'POST', body }),
            invalidatesTags: ['MedicalLab'],
        }),

        updateMedicalLab: builder.mutation<MedicalLab, { id: string; data: UpdateMedicalLabRequest }>({
            query: ({ id, data }) => ({ url: `/medical-labs/${id}`, method: 'PATCH', body: data }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'MedicalLab', id }, 'MedicalLab'],
        }),

        activateMedicalLab: builder.mutation<MedicalLab, string>({
            query: (id) => ({ url: `/medical-labs/${id}/activate`, method: 'PATCH' }),
            invalidatesTags: (_result, _error, id) => [{ type: 'MedicalLab', id }, 'MedicalLab'],
        }),

        deactivateMedicalLab: builder.mutation<MedicalLab, string>({
            query: (id) => ({ url: `/medical-labs/${id}/deactivate`, method: 'PATCH' }),
            invalidatesTags: (_result, _error, id) => [{ type: 'MedicalLab', id }, 'MedicalLab'],
        }),

        // ==================== PROMO CODES (admin) ====================
        createPromoCode: builder.mutation<PromoCode, CreatePromoCodeRequest>({
            query: (body) => ({ url: '/admin/promo-codes', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['PromoCode'],
        }),

        getPromoCodes: builder.query<PromoCode[], { status?: string } | void>({
            query: (args) => ({ url: '/admin/promo-codes', params: args?.status ? { status: args.status } : {} }),
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['PromoCode'],
        }),

        getPromoCode: builder.query<PromoCode, string>({
            query: (id) => `/admin/promo-codes/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'PromoCode', id }],
        }),

        disablePromoCode: builder.mutation<PromoCode, string>({
            query: (id) => ({ url: `/admin/promo-codes/${id}/disable`, method: 'PATCH' }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, id) => [{ type: 'PromoCode', id }, 'PromoCode'],
        }),

        updatePromoCode: builder.mutation<PromoCode, { id: string; data: UpdatePromoCodeRequest }>({
            query: ({ id, data }) => ({ url: `/admin/promo-codes/${id}`, method: 'PATCH', body: data }),
            transformResponse: (response: any) => response?.data ?? response,
            // Raising the cap can flip status back to active, so the list needs
            // busting too — not just the single row.
            invalidatesTags: (_result, _error, { id }) => [{ type: 'PromoCode', id }, 'PromoCode'],
        }),

        getPromoCodeRedemptions: builder.query<PromoRedemption[], string>({
            query: (id) => `/admin/promo-codes/${id}/redemptions`,
            transformResponse: (response: any) => {
                const data = response?.data ?? response;
                // Tolerates both a bare array and a paginated envelope.
                if (Array.isArray(data)) return data;
                return Array.isArray(data?.items) ? data.items : [];
            },
            providesTags: (_result, _error, id) => [{ type: 'PromoCode', id }],
        }),

        // ==================== SELECTION QUOTE (employer) ====================
        // Price a live selection before checkout. Returns exactly what will be
        // charged: omit `features` for the whole plan, or send a subset to see
        // the cherry-pick markup applied. Ids not on the plan are dropped from
        // the echo, so the quote and the charge can never disagree.
        getSelectionQuote: builder.mutation<SelectionQuoteResponse, SelectionQuoteRequest>({
            query: (body) => ({ url: '/payments/quote', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== PROMO QUOTE (employer) ====================
        // Preview a promo: validate + see the discounted amount before paying.
        getPromoQuote: builder.mutation<PromoQuoteResponse, PromoQuoteRequest>({
            query: (body) => ({ url: '/payments/promo/quote', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== MESSAGING (super_admin, customer_success) ====================
        sendSms: builder.mutation<SendSmsResponse, SendSmsRequest>({
            query: (body) => ({ url: '/messages/send-sms', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== ADMIN TRANSACTIONS (super_admin) ====================
        getAdminTransactions: builder.query<AdminTransactionsResponse, AdminTransactionFilters | void>({
            query: (args) => {
                const params: Record<string, string | number> = {};
                if (args) {
                    (Object.keys(args) as (keyof AdminTransactionFilters)[]).forEach((k) => {
                        const v = args[k];
                        if (v !== undefined && v !== '' && v !== null) params[k] = v as string | number;
                    });
                }
                return { url: '/admin/transactions', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['AdminTransaction'],
        }),

        // ==================== BLOG ====================
        // Public: paginated list of published posts (body omitted).
        getBlogs: builder.query<PaginatedBlogs<BlogListItem>, BlogListParams | void>({
            query: (args) => {
                const params: Record<string, string | number> = {};
                if (args) {
                    if (args.page) params.page = args.page;
                    if (args.limit) params.limit = args.limit;
                    if (args.tag) params.tag = args.tag;
                    if (args.search) params.search = args.search;
                }
                return { url: '/blogs', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Blog'],
        }),

        // Public: one published post by slug (increments viewCount).
        getBlogBySlug: builder.query<Blog, string>({
            query: (slug) => `/blogs/${slug}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, slug) => [{ type: 'Blog', id: slug }],
        }),

        // Admin: paginated list of all posts incl. drafts (body omitted).
        getAdminBlogs: builder.query<PaginatedBlogs<BlogListItem>, BlogListParams | void>({
            query: (args) => {
                const params: Record<string, string | number> = {};
                if (args) {
                    if (args.page) params.page = args.page;
                    if (args.limit) params.limit = args.limit;
                    if (args.tag) params.tag = args.tag;
                    if (args.search) params.search = args.search;
                }
                return { url: '/blogs/admin/all', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Blog'],
        }),

        // Admin: one post by id incl. body — seeds the editor.
        getAdminBlogById: builder.query<Blog, string>({
            query: (id) => `/blogs/admin/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'Blog', id }],
        }),

        createBlog: builder.mutation<Blog, CreateBlogRequest>({
            query: (data) => ({
                url: '/blogs',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Blog'],
        }),

        updateBlog: builder.mutation<Blog, { id: string; data: UpdateBlogRequest }>({
            query: ({ id, data }) => ({
                url: `/blogs/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Blog', id }, 'Blog'],
        }),

        publishBlog: builder.mutation<Blog, string>({
            query: (id) => ({
                url: `/blogs/${id}/publish`,
                method: 'POST',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, id) => [{ type: 'Blog', id }, 'Blog'],
        }),

        unpublishBlog: builder.mutation<Blog, string>({
            query: (id) => ({
                url: `/blogs/${id}/unpublish`,
                method: 'POST',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, id) => [{ type: 'Blog', id }, 'Blog'],
        }),

        // Upload/replace the document file for a document post (multipart).
        uploadBlogDocument: builder.mutation<Blog, { id: string; file: File }>({
            query: ({ id, file }) => {
                const fd = new FormData();
                fd.append('document', file);
                return {
                    url: `/blogs/${id}/document`,
                    method: 'POST',
                    body: fd,
                };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Blog', id }, 'Blog'],
        }),

        // Upload/replace the cover image (multipart).
        uploadBlogCover: builder.mutation<Blog, { id: string; file: File }>({
            query: ({ id, file }) => {
                const fd = new FormData();
                fd.append('coverImage', file);
                return {
                    url: `/blogs/${id}/cover`,
                    method: 'POST',
                    body: fd,
                };
            },
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Blog', id }, 'Blog'],
        }),

        deleteBlog: builder.mutation<{ id: string; deleted: boolean }, string>({
            query: (id) => ({
                url: `/blogs/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['Blog'],
        }),

        // ==================== PUBLIC VERIFICATION (QR) ====================
        // Public, no auth. 404 → not a recognized platform member.
        getVerification: builder.query<VerificationResult, string>({
            query: (id) => `/verify/${id}`,
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // ==================== EMPLOYER REFERENCE ====================
        // Public — token from the previous employer's email/SMS. 404 for a bad token.
        getEmployerReference: builder.query<EmployerReferencePublic, string>({
            query: (token) => `/investigations/employer-reference/${token}`,
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // One submission per link — a second POST returns 400 (terminal, not retryable).
        submitEmployerReference: builder.mutation<
            { success?: boolean } | EmployerReferencePublic,
            SubmitEmployerReferenceRequest
        >({
            query: ({ token, ...body }) => ({
                url: `/investigations/employer-reference/${token}`,
                method: 'POST',
                body,
            }),
            transformResponse: (response: any) => response?.data ?? response,
        }),

        // Admin (Super Admin / CS) list of reference requests on an investigation.
        getEmployerReferences: builder.query<EmployerReference[], string>({
            query: (id) => `/investigations/${id}/employer-references`,
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (_result, _error, id) => [{ type: 'EmployerReference', id }],
        }),

        // Re-send email + SMS for a reference (Super Admin). 400 if already responded.
        retriggerEmployerReference: builder.mutation<
            { success?: boolean },
            { referenceId: string; investigationId: string }
        >({
            query: ({ referenceId }) => ({
                url: `/investigations/employer-references/${referenceId}/retrigger`,
                method: 'POST',
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: (_result, _error, { investigationId }) => [
                { type: 'EmployerReference', id: investigationId },
            ],
        }),

        // ==================== EMPLOYEE REPORTS ====================
        // Employer files a derogatory report — Option A (investigationId, pulls
        // verified identity incl. NIN) or Option B (name + one of email/phone/nin).
        fileEmployeeReport: builder.mutation<EmployeeReport, FileEmployeeReportRequest>({
            query: (body) => ({ url: '/employee-reports', method: 'POST', body }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['EmployeeReport'],
        }),

        // Employer: the reports they have filed.
        getMyEmployeeReports: builder.query<EmployeeReport[], void>({
            query: () => '/employee-reports/mine',
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['EmployeeReport'],
        }),

        // Admin / CS: paginated list of all reports (optionally filtered by status).
        getEmployeeReports: builder.query<EmployeeReportsResponse, EmployeeReportListParams | void>({
            query: (args) => {
                const params: Record<string, string | number> = {};
                if (args) {
                    if (args.status) params.status = args.status;
                    if (args.page) params.page = args.page;
                    if (args.limit) params.limit = args.limit;
                }
                return { url: '/employee-reports', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['EmployeeReport'],
        }),

        // Admin / CS: live match check. Each match carries `matchedOn` for confidence.
        scanEmployeeReports: builder.query<EmployeeReportScanMatch[], EmployeeReportScanParams>({
            query: (args) => {
                const params: Record<string, string> = {};
                if (args.name) params.name = args.name;
                if (args.email) params.email = args.email;
                if (args.phone) params.phone = args.phone;
                if (args.nin) params.nin = args.nin;
                return { url: '/employee-reports/scan', params };
            },
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['EmployeeReport'],
        }),

        // Admin / CS: update a report's status (e.g. dismiss with review notes).
        updateEmployeeReportStatus: builder.mutation<EmployeeReport, UpdateEmployeeReportStatusRequest>({
            query: ({ id, ...body }) => ({
                url: `/employee-reports/${id}/status`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (response: any) => response?.data ?? response,
            invalidatesTags: ['EmployeeReport'],
        }),
    }),
});

// Export hooks for usage in functional components
export const {
    // Auth
    useLoginMutation,
    useLoginVerifyMutation,
    useRegisterMutation,
    useGetProfileQuery,
    useLazyGetProfileQuery,
    useSendOtpMutation,
    useVerifyOtpMutation,
    useSendEmailVerificationMutation,
    useVerifyEmailMutation,
    // Investigations
    useValidateInvestigationMutation,
    useCreateInvestigationMutation,
    useCreateInvestigationBatchMutation,
    useGetMyInvestigationsQuery,
    useGetInvestigationByIdQuery,
    useGetEmployerReportQuery,
    useGetAddressVisitsQuery,
    useCreateAddressVisitMutation,
    useDeleteAddressVisitMutation,
    useGetChecklistCatalogQuery,
    useGetCheckTypeBriefsQuery,
    useGetInvestigationAgentBriefQuery,
    useGetInvestigationChecklistQuery,
    useSaveInvestigationChecklistMutation,
    useGetInvestigationMilestonesQuery,
    useSaveMilestoneDraftMutation,
    useSubmitMilestoneMutation,
    useRemoveMilestoneSectionMutation,
    useAddMilestoneSectionMutation,
    useGetInvestigationByLinkQuery,
    useGetConsentDraftQuery,
    useSaveConsentDraftMutation,
    useSubmitEmployeeFormMutation,
    useDeleteInvestigationMutation,
    useRetriggerConsentFormMutation,
    // Information requests (missing info) + nudge
    useGetRequestableFieldsQuery,
    useGetInformationRequestsQuery,
    useCreateInformationRequestMutation,
    useGetInformationRequestByTokenQuery,
    useSubmitInformationRequestMutation,
    useNudgeInvestigationMutation,
    // Guarantors
    useSubmitGuarantorsMutation,
    useGetGuarantorRecommendationQuery,
    useSubmitGuarantorRecommendationMutation,
    useGetGuarantorIndemnityFormQuery,
    useSubmitGuarantorIndemnityMutation,
    useGetInvestigationGuarantorsQuery,
    useGetAvailableAgentsForGuarantorQuery,
    useReassignGuarantorMutation,
    useUpdateGuarantorVerificationMutation,
    useGetInvestigationStatsQuery,
    useGetInvestigationTimerQuery,
    useGetSlaSummaryQuery,
    useAssignAgentMutation,
    useStartInvestigationMutation,
    useSubmitReportMutation,
    useUpdateInvestigationReportMutation,
    useValidateInvestigationReportMutation,
    useUploadInvestigationDocumentMutation,
    useGetInvestigationDocumentsQuery,
    useValidateRequiredDocumentsQuery,
    useDeleteDocumentMutation,
    useGetScoringCriteriaQuery,
    useSubmitRiskAssessmentMutation,
    useGetRiskAssessmentQuery,
    useGetRiskAssessmentHistoryQuery,
    useUploadAgentDocumentMutation,
    useUploadMultipleAgentDocumentsMutation,
    useApproveDocumentMutation,
    useGetFilteredDocumentsQuery,
    // Payments
    useGetCheckOptionsQuery,
    useInitiatePaymentMutation,
    useVerifyPaymentMutation,
    useGetMyTransactionsQuery,
    useGetTransactionByIdQuery,
    useGetReusablePaymentsQuery,
    // Earnings
    useGetMyEarningsQuery,
    useGetMyEarningsSummaryQuery,
    useGetEarningsBreakdownQuery,
    // Analytics
    useGetDashboardStatsQuery,
    useGetInvestigationTrendsQuery,
    useGetRevenueTrendsQuery,
    useGetTopAgentsQuery,
    useGetStatusDistributionQuery,
    useGetFinancialStatsQuery,
    // Admin
    useInviteAgentMutation,
    useCreateStaffMutation,
    useAdminAssignCustomerSuccessMutation,
    useAdminAssignMedicalUserMutation,
    useAdminAssignNotaryMutation,
    useAdminAssignPoliceMutation,
    useGetPricingQuery,
    useUpdatePricingMutation,
    useGetCheckAddonsQuery,
    useCreateCheckAddonMutation,
    useUpdateCheckAddonMutation,
    useDeleteCheckAddonMutation,
    useReorderCheckAddonsMutation,
    useReseedCheckAddonsMutation,
    useGetSelectionQuoteMutation,
    useGetPlanFeaturesQuery,
    useCreatePlanFeatureMutation,
    useUpdatePlanFeatureMutation,
    useDeletePlanFeatureMutation,
    useGetAdminPlansQuery,
    useUpdatePlanMutation,
    useGetAllUsersQuery,
    useGetUserByIdQuery,
    useUpdateUserStatusMutation,
    useSetPaymentExemptionMutation,
    useDeleteUserMutation,
    useGetAllInvestigationsQuery,
    useReassignInvestigationMutation,
    useGetPlatformStatisticsQuery,
    useGetAllEarningsQuery,
    useGetPlatformEarningsStatsQuery,
    useMarkEarningAsPaidMutation,
    useBulkMarkEarningsAsPaidMutation,
    // Waitlist
    useRequestCallbackMutation,
    useGetCallbacksQuery,
    useUpdateCallbackStatusMutation,
    useJoinWaitlistMutation,
    useGetAllWaitlistEntriesQuery,
    useGetWaitlistStatsQuery,
    useCheckEmailOnWaitlistQuery,
    useGetWaitlistEntryByIdQuery,
    useUpdateWaitlistEntryMutation,
    useBulkUpdateWaitlistStatusMutation,
    useDeleteWaitlistEntryMutation,
    // Support Tickets
    useCreateSupportTicketMutation,
    useGetMyTicketsQuery,
    useGetMyTicketByIdQuery,
    useGetAllTicketsQuery,
    useGetTicketByIdQuery,
    useUpdateTicketStatusMutation,
    useUpdateTicketPriorityMutation,
    useAssignTicketMutation,
    useGetTicketStatisticsQuery,
    useOpenEmployeeTicketMutation,
    useGetEmployerTicketsQuery,
    useGetEmployerTicketQuery,
    useOpenEmployerTicketMutation,
    useReplyEmployerTicketMutation,
    useListEmployeeTicketsQuery,
    useGetEmployeeTicketQuery,
    useReplyEmployeeTicketMutation,
    useReplyAdminTicketMutation,
    // Bank Accounts
    useAddBankAccountMutation,
    useGetMyBankAccountsQuery,
    useGetMyBankAccountByIdQuery,
    useUpdateBankAccountMutation,
    useSetDefaultBankAccountMutation,
    useDeleteBankAccountMutation,
    useGetAllBankAccountsQuery,
    // Withdrawals
    useCreateWithdrawalMutation,
    useGetMyWithdrawalsQuery,
    useGetMyWithdrawalByIdQuery,
    useGetAllWithdrawalsQuery,
    useGetWithdrawalByIdQuery,
    useReviewWithdrawalMutation,
    useProcessWithdrawalMutation,
    useGetWithdrawalStatisticsQuery,
    // HR Referral Program
    useGetHrMeQuery,
    useGetHrReferralsQuery,
    useGetHrEarningsQuery,
    useGetHrEarningsSummaryQuery,
    useGetHrLeaderboardQuery,
    // Training — agent
    useGetTrainingCoursesQuery,
    useGetTrainingCourseQuery,
    useEnrollTrainingCourseMutation,
    useMarkVideoWatchedMutation,
    useGetVideoQuizQuery,
    useLazyGetVideoQuizQuery,
    useSubmitQuizMutation,
    useGetMyCertificatesQuery,
    useGetCourseCertificateQuery,
    // Training — admin
    useAdminCreateCourseMutation,
    useAdminListCoursesQuery,
    useAdminGetCourseQuery,
    useAdminUpdateCourseMutation,
    useAdminDeleteCourseMutation,
    useAdminAddVideoLinkMutation,
    useAdminUploadVideoMutation,
    useAdminUpdateVideoMutation,
    useAdminDeleteVideoMutation,
    useAdminAddQuestionMutation,
    useAdminUpdateQuestionMutation,
    useAdminDeleteQuestionMutation,
    // Contact messages
    useSubmitContactMessageMutation,
    useGetContactMessagesQuery,
    useUpdateContactMessageStatusMutation,
    useDeleteContactMessageMutation,
    // Customer Success
    useGetCsMetricsQuery,
    useGetCsInvestigationsQuery,
    useGetCsInvestigationQuery,
    useCsAssignAgentMutation,
    useCsAssignMedicalMutation,
    useGetCsChecklistQuery,
    useUpdateCsChecklistMutation,
    useCsNotifyEmployeeMutation,
    useCsUpdateEmployerMutation,
    useCsReassignGuarantorMutation,
    useGetCsAssignableUsersQuery,
    // Medical
    useGetMedicalMetricsQuery,
    useGetMedicalInvestigationsQuery,
    useGetMedicalInvestigationQuery,
    useGetNotaryInvestigationsQuery,
    useGetNotaryInvestigationQuery,
    useUploadFinalAffidavitMutation,
    useGetPoliceInvestigationsQuery,
    useGetPoliceInvestigationQuery,
    useUploadPoliceCharacterCertificateMutation,
    useGetMedicalTestsQuery,
    useGetMedicalReportQuery,
    useSubmitMedicalReportMutation,
    // Medical labs
    useGetMedicalLabsQuery,
    useGetMedicalLabQuery,
    useMedicalAssignLabMutation,
    useCsAssignLabMutation,
    useCreateMedicalLabMutation,
    useUpdateMedicalLabMutation,
    useActivateMedicalLabMutation,
    useDeactivateMedicalLabMutation,
    // Promo codes
    useCreatePromoCodeMutation,
    useUpdatePromoCodeMutation,
    useGetPromoCodeRedemptionsQuery,
    useGetPromoCodesQuery,
    useGetPromoCodeQuery,
    useDisablePromoCodeMutation,
    useGetPromoQuoteMutation,
    // Messaging
    useSendSmsMutation,
    // Admin transactions
    useGetAdminTransactionsQuery,
    // Blog
    useGetBlogsQuery,
    useGetBlogBySlugQuery,
    useGetAdminBlogsQuery,
    useGetAdminBlogByIdQuery,
    useCreateBlogMutation,
    useUpdateBlogMutation,
    usePublishBlogMutation,
    useUnpublishBlogMutation,
    useUploadBlogDocumentMutation,
    useUploadBlogCoverMutation,
    useDeleteBlogMutation,
    // Public verification
    useGetVerificationQuery,
    // Employer reference
    useGetEmployerReferenceQuery,
    useSubmitEmployerReferenceMutation,
    useGetEmployerReferencesQuery,
    useRetriggerEmployerReferenceMutation,
    // Employee reports (derogatory reports)
    useFileEmployeeReportMutation,
    useGetMyEmployeeReportsQuery,
    useGetEmployeeReportsQuery,
    useScanEmployeeReportsQuery,
    useUpdateEmployeeReportStatusMutation,
} = api;
